import { Flex } from "antd";
import dayjs from "dayjs";
import {
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useMemo,
    useState,
} from "react";
import { useParams } from "react-router-dom";
import { Invoice } from "tm-shared/types";

import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { useAppSelector } from "@/hooks/redux";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";

import { InvoiceCard } from "../InvoiceCard";
import { InvoiceInfoModal } from "../InvoiceInfoModal";
import { StyledDebtDropdown, StyledDebtDropdownHeader } from "../Styled";

interface ByContragentProps {
    selectedDates: string[];
    setSelectedDates: (dates: string[]) => void;
    selectedContragentInvoiceIds: string[];
    setSelectedContragentInvoiceIds: Dispatch<SetStateAction<string[]>>;
    loading: boolean;
}

const formatDate = (date: string) => dayjs(date).format("DD.MM.YYYY");

export const ByContragent: FC<ByContragentProps> = ({
    selectedDates,
    setSelectedDates,
    selectedContragentInvoiceIds,
    setSelectedContragentInvoiceIds,
    loading,
}) => {
    const { id } = useParams();
    const safeId = id ?? "";

    const { debtsByFOP } = useAppSelector((state) => state.offline);
    const fopDebtByDate = useMemo(
        () => debtsByFOP?.[safeId] ?? {},
        [debtsByFOP, safeId],
    );

    const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(
        null,
    );

    const invoicesOnSelectedDates = useMemo(
        () => selectedDates.flatMap((date) => fopDebtByDate?.[date] ?? []),
        [selectedDates, fopDebtByDate],
    );

    const consignmentOptions = useMemo(
        () =>
            invoicesOnSelectedDates
                .filter(
                    (inv) =>
                        !selectedContragentInvoiceIds.includes(
                            inv.consignmentId,
                        ),
                )
                .map((inv) => ({
                    label: (
                        <Flex vertical gap="10px" style={{ padding: "12px" }}>
                            <PrimaryText
                                fontSize={18}
                                style={{ textWrap: "wrap" }}
                            >
                                {inv.address}
                            </PrimaryText>

                            <SecondaryText
                                fontSize={18}
                                style={{ textWrap: "wrap" }}
                            >
                                {inv.consignmentId}
                            </SecondaryText>

                            <Flex justify="space-between">
                                <PrimaryText
                                    fontSize={18}
                                    style={{ textWrap: "wrap" }}
                                >
                                    {inv?.shipmentDate
                                        ? formatDate(inv.shipmentDate)
                                        : "-"}
                                </PrimaryText>
                                <PrimaryText
                                    fontSize={18}
                                    $textColor="accent"
                                    style={{
                                        textWrap: "wrap",
                                    }}
                                >
                                    {`${inv.debtAmount.toFixed(2)} грн`}
                                </PrimaryText>
                            </Flex>
                        </Flex>
                    ),
                    value: inv.consignmentId,
                })),
        [invoicesOnSelectedDates, selectedContragentInvoiceIds],
    );

    const selectedInvoices = useMemo<Invoice[]>(() => {
        return invoicesOnSelectedDates.filter((inv): inv is Invoice =>
            selectedContragentInvoiceIds.includes(inv.consignmentId),
        );
    }, [invoicesOnSelectedDates, selectedContragentInvoiceIds]);

    const handleDatesChange = (dates: string[]) => {
        setSelectedDates(dates);
        setSelectedContragentInvoiceIds([]);
        setSelectedInvoice(null);
    };

    const handleInvoiceSelect = (consignmentId: string) => {
        setSelectedContragentInvoiceIds((prev) =>
            prev.includes(consignmentId) ? prev : [...prev, consignmentId],
        );
    };

    const handleInvoiceRemove = (consignmentId: string) => {
        if (loading) return;

        setSelectedContragentInvoiceIds((prev) =>
            prev.filter((id) => id !== consignmentId),
        );
        setSelectedInvoice((prev) =>
            prev?.consignmentId === consignmentId ? null : prev,
        );
    };

    const handleInvoiceInfoClick = (invoice: Invoice) => {
        setSelectedInvoice(invoice);
    };

    const handleModalClose = useCallback(() => {
        setSelectedInvoice(null);
    }, []);

    const handleCopy = useCallback(
        (invoice: Invoice) => copyObjectToClipboard(invoice),
        [],
    );

    return (
        <>
            <Flex vertical gap={16}>
                <Flex vertical gap={4}>
                    <PrimaryText>Оберіть дату</PrimaryText>

                    <StyledDebtDropdownHeader
                        options={Object.keys(fopDebtByDate || {}).map(
                            (date) => ({
                                label: formatDate(date),
                                value: date,
                            }),
                        )}
                        mode="multiple"
                        allowClear
                        value={selectedDates}
                        onChange={handleDatesChange}
                        placeholder="Оберіть дату"
                        disabled={loading}
                    />
                </Flex>

                <Flex vertical gap={4}>
                    <PrimaryText>Оберіть накладні</PrimaryText>

                    <StyledDebtDropdownHeader
                        popupRender={(menu) => (
                            <StyledDebtDropdown>{menu}</StyledDebtDropdown>
                        )}
                        options={consignmentOptions}
                        mode="multiple"
                        value={[]}
                        onSelect={(value: string) => handleInvoiceSelect(value)}
                        allowClear
                        disabled={selectedDates.length === 0 || loading}
                        placeholder="Оберіть накладні"
                    />
                </Flex>

                <Flex vertical gap={8}>
                    {selectedInvoices.map((inv) => (
                        <InvoiceCard
                            key={inv.consignmentId}
                            consignmentId={inv.consignmentId}
                            shipmentDate={inv.shipmentDate}
                            debt={inv.debtAmount}
                            onInfo={() => handleInvoiceInfoClick(inv)}
                            onRemove={() =>
                                handleInvoiceRemove(inv.consignmentId)
                            }
                            address={inv.address}
                            contragent={inv.contragent}
                            onCopy={() => handleCopy(inv)}
                        />
                    ))}
                </Flex>
            </Flex>

            <InvoiceInfoModal
                onClose={handleModalClose}
                selectedInvoice={selectedInvoice}
                open={!!selectedInvoice}
                debt={selectedInvoice?.debtAmount ?? 0}
                shipmentDate={selectedInvoice?.shipmentDate ?? ""}
            />
        </>
    );
};
